import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { categorizeFiles, getDateObjecttoMMMDDYYYY } from '../../../../helpers/common';
import { MessageDetailsInterface } from '../../../../interfaces/chatMessageInterface';
import MessageBubble from '../messageBubble';
import MessageLoading from '../../../loading/messageLoading';
import { DiscussDiv, LeftDiv, MessagesDivMain, OlderMessagesLoader, ScrollRef, TopDiv } from '../styles';
import AppLoading from '../../../loading/appLoading';
import EmptyStateV2 from '../../../emptyState';
import { EMPTY_STATE_SIZE } from '../../../../global/constants';
import { useMessageFeedEnvironment, useMessageSelectionContext } from '../MessageFeedContext';
import { loadOlderOpenChatMessages, loadOlderProjectMessages } from '../../../../services/conversationSyncService';

interface MessageListProps {
  messages: MessageDetailsInterface[];
  loading: boolean;
  displayedMessages: MessageDetailsInterface[];
  setDisplayedMessages: React.Dispatch<React.SetStateAction<MessageDetailsInterface[]>>;
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  isMessageLoading?: boolean;
  newGroupState: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  isBubbleMenuOpen: boolean;
  subNavigationHeight: number;
  reactionCountsByMessageId?: Record<string, Record<string, number>>;
  // Older pages exist on the server (read by the observer parent; this component isn't one).
  canLoadOlder: boolean;
  // An older page is being fetched from the server.
  isLoadingOlder: boolean;
  // Failed older-page loads so far; part of InfiniteScroll's dataLength so it retries on scroll.
  loadOlderFailures: number;
  children?: React.ReactNode;
}

interface MessageDisplayMeta {
  formatedDate: string | null;
  flag: boolean;
  isFirstUserMessage: boolean;
  isShowTime: boolean;
  isShowUserImage: boolean;
}

const BATCH_SIZE = 20;

function computeMessageDisplayMeta(displayedMessages: MessageDetailsInterface[]): MessageDisplayMeta[] {
  let dateRef: string | null = null;
  let messageTime = '';

  return displayedMessages.map((item, index) => {
    const formatedDate = getDateObjecttoMMMDDYYYY(item?.createdOn);
    let flag = false;
    if (formatedDate !== dateRef) {
      flag = true;
      dateRef = formatedDate;
    }

    const isFirstUserMessage = flag || index === 0 || item?.sender?.id !== displayedMessages?.[index - 1]?.sender?.id;

    let isShowTime = false;
    if (isFirstUserMessage || new Date(item?.createdOn).getTime() - new Date(messageTime).getTime() > 5 * 60 * 1000) {
      messageTime = item?.createdOn;
      isShowTime = true;
    }

    const nextMessageDate =
      displayedMessages?.[index + 1]?.createdOn && getDateObjecttoMMMDDYYYY(displayedMessages?.[index + 1]?.createdOn);

    const isShowUserImage =
      nextMessageDate !== formatedDate ||
      item?.sender?.id !== displayedMessages?.[index + 1]?.sender?.id ||
      new Date(displayedMessages[index + 1]?.createdOn).getTime() - new Date(messageTime).getTime() > 5 * 60 * 1000;

    return { formatedDate, flag, isFirstUserMessage, isShowTime, isShowUserImage };
  });
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  loading,
  displayedMessages,
  setDisplayedMessages,
  currentIndex,
  setCurrentIndex,
  isMessageLoading,
  newGroupState,
  containerRef,
  isBubbleMenuOpen,
  subNavigationHeight,
  reactionCountsByMessageId,
  canLoadOlder,
  isLoadingOlder,
  loadOlderFailures,
  children
}) => {
  const { isNavbarMessage, isProjectMessageDetail } = useMessageFeedEnvironment();
  const { emojiButtonRef } = useMessageSelectionContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<React.RefObject<HTMLDivElement>[]>([]);

  const firstMessageIdRef = useRef<string>();

  const displayMeta = useMemo(() => computeMessageDisplayMeta(displayedMessages), [displayedMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      inline: 'nearest',
      block: 'nearest'
    });
  }, []);

  useEffect(() => {
    const previousFirstId = firstMessageIdRef.current;
    firstMessageIdRef.current = messages?.[0]?.id;
    if (messages?.length > 0) {
      const prependedCount = previousFirstId ? messages.findIndex((message) => message?.id === previousFirstId) : -1;
      // An older page was prepended while the top was reached: reveal the next batch above it
      // instead of resetting the view to the newest messages.
      const newIndex =
        prependedCount > 0 && currentIndex === 0
          ? Math.max(prependedCount - BATCH_SIZE, 0)
          : Math.max(messages.length - BATCH_SIZE, 0);
      setDisplayedMessages(messages.slice(newIndex));
      setCurrentIndex(newIndex);
    } else {
      setDisplayedMessages([]);
      setCurrentIndex(0);
    }
  }, [messages?.length, messages?.[0]?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Messages updated in place (another user's reaction or edit) keep the same window: swap the
  // new copies into the displayed ones by id.
  useEffect(() => {
    const latestById = new Map((messages || []).map((message) => [message.id, message]));
    setDisplayedMessages((displayed) => {
      let changed = false;
      const next = displayed.map((message) => {
        const latest = latestById.get(message.id);
        if (latest && latest !== message) {
          changed = true;
          return latest;
        }
        return message;
      });
      return changed ? next : displayed;
    });
  }, [messages, setDisplayedMessages]);

  const loadMoreMessages = useCallback(() => {
    if (currentIndex === 0 && canLoadOlder) {
      if (isNavbarMessage) loadOlderOpenChatMessages();
      else loadOlderProjectMessages();
      return;
    }
    const newIndex = Math.max(currentIndex - BATCH_SIZE, 0);
    setDisplayedMessages(messages?.slice(newIndex, messages?.length));
    setCurrentIndex(newIndex);
  }, [canLoadOlder, currentIndex, isNavbarMessage, messages, setCurrentIndex, setDisplayedMessages]);

  // Pre-allocate refs for message items when the number of displayed messages changes.
  // Must use useMemo (not useEffect) so refs exist during render when passed as ref props.
  useMemo(() => {
    const size = displayedMessages?.length || 0;
    [buttonRef, emojiButtonRef].forEach((ref) => {
      if (ref.current.length < size) {
        for (let i = ref.current.length; i < size; i++) {
          ref.current[i] = React.createRef<HTMLDivElement>();
        }
      } else if (ref.current.length > size) {
        ref.current.length = size;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedMessages?.length]);

  return (
    <LeftDiv id='scrollable-container' ref={containerRef} isOverflowHidden={isBubbleMenuOpen}>
      <MessagesDivMain>
        <DiscussDiv responsiveBottomSpacing={`${subNavigationHeight}px`}>
          {loading && (messages?.length === 0 || !messages) && (
            <MessageLoading
              isMessagesLoading={isNavbarMessage ? true : false}
              isWidth={isNavbarMessage ? true : false}
              isProjectMessageDetail={isProjectMessageDetail}
            />
          )}

          {messages?.length > 0 && !newGroupState && (
            <TopDiv>
              {isLoadingOlder && (
                <OlderMessagesLoader role='status' aria-label='Loading older messages'>
                  <AppLoading size={20} isInline />
                </OlderMessagesLoader>
              )}
              {loading && (messages?.length === 0 || !messages) && (
                <MessageLoading isMessagesLoading={isMessageLoading} isProjectMessageDetail={isProjectMessageDetail} />
              )}
              <InfiniteScroll
                // InfiniteScroll only calls `next` again after dataLength changes.
                dataLength={(displayedMessages?.length || 0) + loadOlderFailures}
                next={loadMoreMessages}
                hasMore={currentIndex > 0 || canLoadOlder}
                // Server pages show OlderMessagesLoader above; revealing loaded batches is instant.
                loader={<></>}
                endMessage={<></>}
                scrollableTarget='scrollable-container'
                scrollThreshold={0.7}
                inverse={true}>
                {displayedMessages?.map((item, index: number) => {
                  const { imageFiles, videoFiles, audioFiles, otherFiles } = categorizeFiles(item?.uploadedFile);
                  const meta = displayMeta[index];

                  return (
                    <MessageBubble
                      key={item?.id}
                      item={item}
                      index={index}
                      isFirstUserMessage={meta?.isFirstUserMessage}
                      isShowTime={meta?.isShowTime}
                      isShowUserImage={meta?.isShowUserImage}
                      formatedDate={meta?.formatedDate || ''}
                      flag={meta?.flag}
                      imageFiles={imageFiles}
                      videoFiles={videoFiles}
                      audioFiles={audioFiles}
                      otherFiles={otherFiles}
                      reactionCounts={reactionCountsByMessageId?.[item.id]}
                    />
                  );
                })}
              </InfiniteScroll>
              <ScrollRef ref={messagesEndRef} />
            </TopDiv>
          )}
        </DiscussDiv>
        {!loading && messages?.length === 0 && (
          <EmptyStateV2
            size={EMPTY_STATE_SIZE.MEDIUM}
            title='No messages at the moment.'
            description='Type below to add your message.'
            imageUrl={'chat_empty'}
            isAlignedCenter={true}
          />
        )}
        {children}
      </MessagesDivMain>
    </LeftDiv>
  );
};

function areEqualMessageList(prevProps: MessageListProps, nextProps: MessageListProps) {
  return (
    prevProps.messages === nextProps.messages &&
    prevProps.loading === nextProps.loading &&
    prevProps.displayedMessages === nextProps.displayedMessages &&
    prevProps.currentIndex === nextProps.currentIndex &&
    prevProps.isMessageLoading === nextProps.isMessageLoading &&
    prevProps.newGroupState === nextProps.newGroupState &&
    prevProps.containerRef === nextProps.containerRef &&
    prevProps.isBubbleMenuOpen === nextProps.isBubbleMenuOpen &&
    prevProps.subNavigationHeight === nextProps.subNavigationHeight &&
    prevProps.reactionCountsByMessageId === nextProps.reactionCountsByMessageId &&
    prevProps.canLoadOlder === nextProps.canLoadOlder &&
    prevProps.isLoadingOlder === nextProps.isLoadingOlder &&
    prevProps.loadOlderFailures === nextProps.loadOlderFailures &&
    prevProps.children === nextProps.children
  );
}

export default React.memo(MessageList, areEqualMessageList);
